import { pulseArticles, type PulseArticle } from '../content/pulse';
import { pressReleases, type PressRelease } from '../content/press';

export const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://snsintel.com';

export type ArticleSchemaType = 'Article' | 'NewsArticle';

export interface ArticleSeoMeta {
  schemaType: ArticleSchemaType;
  headline: string;
  /** ISO 8601 publish date (YYYY-MM-DD). */
  datePublished: string;
  authorName: string;
  publisherName: string;
  /** Section name (e.g., `Pulse` / `Press`). */
  section: string;
  keywords: string[];
}

export interface SEOConfig {
  title: string;
  description: string;
  canonical?: string;
  noindex?: boolean;
  /** Optional absolute URL for OG image. */
  ogImage?: string;
  /** Per-article structured-data hints — present on `/pulse/<slug>` and `/press/<slug>`. */
  articleMeta?: ArticleSeoMeta;
}

const PULSE_INTRO_DESCRIPTION =
  "Pulse is SNS Intelligence's strategic briefing for life sciences and health care decision-makers — analyst-driven perspective on the trends, market shifts, pipeline developments, and competitive dynamics shaping pharma, biotech, and health care.";

export const SEO_CONFIG: Record<string, SEOConfig> = {
  '/': {
    title: 'SNS Intelligence | Pharmaceutical Market Intelligence',
    description: 'Comprehensive pharmaceutical market intelligence platform providing structured data, market analysis, and pipeline insights. Access controlled market and competitive intelligence for strategic decision-making.',
  },
  '/platform': {
    title: 'Platform | SNS Intelligence',
    description: 'Advanced analytical platform for pharmaceutical market intelligence. Structured decision support tools for market analysis, pipeline tracking, and competitive intelligence.',
  },
  '/data-coverage': {
    title: 'Data Coverage | SNS Intelligence',
    description: 'Comprehensive pharmaceutical data coverage including market intelligence, drug pipeline analysis, competitive insights, and geographic market data across therapeutic areas.',
  },
  '/markets': {
    title: 'Markets | SNS Intelligence',
    description: 'Pharmaceutical market intelligence pages providing strategic insights, pipeline analysis, and competitive context for pharmaceutical markets across multiple therapeutic areas.',
  },
  '/markets/dermatology': {
    title: 'Dermatology Market Intelligence | SNS Intelligence',
    description: 'Comprehensive dermatology market intelligence including market size, growth trends, pipeline analysis, and competitive landscape for dermatological therapeutics.',
  },
  '/markets/psoriasis': {
    title: 'Psoriasis Market Intelligence | SNS Intelligence',
    description: 'Psoriasis market intelligence covering market dynamics, pipeline developments, competitive analysis, and strategic insights for psoriasis therapeutics.',
  },
  '/markets/skin-cancer': {
    title: 'Skin Cancer Market Intelligence | SNS Intelligence',
    description: 'Skin cancer market intelligence providing market analysis, pipeline tracking, competitive landscape, and strategic insights for skin cancer therapeutics.',
  },
  '/about': {
    title: 'About | SNS Intelligence',
    description: 'Learn about SNS Intelligence, an initiative by SNS Research providing structured pharmaceutical market intelligence methodologies for professional users.',
  },
  '/contact': {
    title: 'Contact | SNS Intelligence',
    description: 'Contact the SNS Intelligence team for platform access, inquiries, and support. Reach out for information about pharmaceutical market intelligence solutions.',
  },
  '/pulse': {
    title: 'Pulse | SNS Intelligence',
    description: PULSE_INTRO_DESCRIPTION,
  },
  '/press': {
    title: 'Press | SNS Intelligence',
    description: 'Official announcements, product updates, and company news from SNS Intelligence — including platform launches, module expansions, and strategic milestones.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | SNS Intelligence',
    description: 'Privacy policy outlining how SNS Intelligence collects, uses, and protects user data in compliance with applicable privacy regulations.',
  },
  '/terms-of-service': {
    title: 'Terms of Service | SNS Intelligence',
    description: 'Terms of service governing the use of SNS Intelligence platform and pharmaceutical market intelligence services.',
  },
  '/login': {
    title: 'Login | SNS Intelligence',
    description: 'Login to access SNS Intelligence platform.',
    noindex: true,
  },
  '/admin': {
    title: 'Admin Portal | SNS Intelligence',
    description: 'Administrative portal for SNS Intelligence.',
    noindex: true,
  },
  '/outreach': {
    title: 'Outreach | SNS Intelligence',
    description: 'Email outreach management portal for SNS Intelligence.',
    noindex: true,
  },
  '/subscribe': {
    title: 'Subscribe | SNS Intelligence',
    description: 'Subscribe to SNS Intelligence platform.',
    noindex: true,
  },
  '/subscription-pending': {
    title: 'Subscription Pending | SNS Intelligence',
    description: 'Your subscription request is pending review.',
    noindex: true,
  },
};

const truncateForMeta = (input: string, max = 160): string => {
  if (input.length <= max) return input;
  const sliced = input.slice(0, max - 1).trimEnd();
  return `${sliced}…`;
};

const buildPulseArticleSeo = (article: PulseArticle): SEOConfig => ({
  title: `${article.title} | SNS Pulse`,
  description: truncateForMeta(article.excerpt),
  articleMeta: {
    schemaType: 'Article',
    headline: article.title,
    datePublished: article.date,
    authorName: 'SNS Intelligence',
    publisherName: 'SNS Intelligence',
    section: 'Pulse',
    keywords: article.keywords,
  },
});

const buildPressReleaseSeo = (release: PressRelease): SEOConfig => ({
  title: `${release.title} | SNS Intelligence Press`,
  description: truncateForMeta(release.excerpt),
  articleMeta: {
    schemaType: 'NewsArticle',
    headline: release.title,
    datePublished: release.date,
    authorName: 'SNS Intelligence',
    publisherName: 'SNS Intelligence',
    section: 'Press',
    keywords: release.keywords,
  },
});

export const getSEOConfig = (pathname: string): SEOConfig => {
  const normalizedPath = pathname === '/' ? '/' : pathname.replace(/\/$/, '');

  if (SEO_CONFIG[normalizedPath]) {
    return SEO_CONFIG[normalizedPath];
  }

  if (normalizedPath.startsWith('/pulse/')) {
    const slug = normalizedPath.slice('/pulse/'.length);
    const article = pulseArticles.find((entry) => entry.slug === slug);
    if (article) {
      return buildPulseArticleSeo(article);
    }
    return {
      title: 'Pulse | SNS Intelligence',
      description: 'The requested Pulse briefing could not be found.',
      noindex: true,
    };
  }

  if (normalizedPath.startsWith('/press/')) {
    const slug = normalizedPath.slice('/press/'.length);
    const release = pressReleases.find((entry) => entry.slug === slug);
    if (release) {
      return buildPressReleaseSeo(release);
    }
    return {
      title: 'Press | SNS Intelligence',
      description: 'The requested press release could not be found.',
      noindex: true,
    };
  }

  if (normalizedPath === '/app' || normalizedPath.startsWith('/app/')) {
    return {
      title: 'Dashboard | SNS Intelligence',
      description: 'SNS Intelligence dashboard.',
      noindex: true,
    };
  }

  return {
    title: 'Page Not Found | SNS Intelligence',
    description: 'The page you are looking for could not be found.',
    noindex: true,
  };
};

export const getCanonicalUrl = (pathname: string): string => {
  const normalizedPath = pathname === '/' ? '/' : pathname.replace(/\/$/, '');
  return `${SITE_URL}${normalizedPath}`;
};
